// Specialty Model

import { Model } from '@vuex-orm/core'
import Specialty from './Specialty'
import { Option } from './Specialty'

export class GeneralSurgery extends Specialty {
  static entity = 'generalSurgery'
  static baseEntity = 'specialties'

  static fields() {
    return {
      ...super.fields(),
      organTree: this.attr({})
    }
  }

  static getLabel(log, component) {
    const item = log[component.id]

    if (component.id === 'surgery-procedure') {
      if (!item) return ''
      return item
        .map(
          procedure =>
            (
              SurgeryProcedure.query()
                .whereId(procedure.id)
                .first() || {}
            ).label || []
        )
        .join('\n')
    } else if (component.id === 'surgery-procedure-ids') {
      if (!log['surgery-procedure']) return ''
      return log['surgery-procedure']
        .map(procedure => {
          const procedureObj =
            SurgeryProcedure.query()
              .whereId(procedure.id)
              .first() || {}
          const procedureName = procedureObj.label || ''
          const procedureQualifiers = (procedure.qualifiers || []).map(
            q =>
              (
                DiagnosisQualifiers.query()
                  .whereId(q)
                  .first() || {}
              ).label || ''
          )
          if (!procedureName) return ''
          if (procedureQualifiers.length < 1) return procedureName
          return `${procedureName}: ${procedureQualifiers.join(', ')}`
        })
        .join('\n')
    } else if (component.id === 'classification-benign-suspicious-malignant') {
      if (!item) return ''
      return (
        (
          Malignancy.query()
            .whereId(item)
            .first() || {}
        ).name || ''
      )
    } else if (component.id === 'surgery-diagnosis') {
      if (!item) return ''
      return Array.isArray(item)
        ? item
            .map(
              diagnosis =>
                (
                  SurgeryDiagnosis.query()
                    .whereId(diagnosis.id)
                    .first() || {}
                ).label || []
            )
            .join('\n')
        : (
            SurgeryDiagnosis.query()
              .whereId(item)
              .first() || {}
          ).label || ''
    } else if (component.id === 'surgery-diagnosis-qualifiers') {
      if (!item) return ''
      return item
        .map(
          qualifierId =>
            (
              DiagnosisQualifiers.query()
                .whereId(qualifierId)
                .first() || {}
            ).label || ''
        )
        .join(', ')
    } else if (component.id === 'surgery-autonomy') {
      const logAutonomy = log.autonomy
      if (logAutonomy) {
        return (
          (
            Option.query()
              .where('component', 'surgery-autonomy')
              .where(opt => opt.value === logAutonomy)
              .first() || {}
          ).text || ''
        )
      }
      return ''
    } else if (component.id === 'anatomic-region') {
      return this.getAnatomicRegionFromTree(item)
    } else {
      return super.getLabel(log, component)
    }
  }

  static get flattedOrganTree() {
    const tree = GeneralSurgery.query().first().organTree
    let options = []
    const traverser = function(nodes) {
      for (const node of nodes) {
        if (node.children) {
          const childrenResult = traverser(node.children)
          if (childrenResult) return childrenResult
        } else {
          options.push(node)
        }
      }
      return null
    }
    traverser(tree)
    return options.map(option => ({ value: option.id, text: option.label }))
  }

  static getAnatomicRegionFromTree(diagnosis) {
    const tree = GeneralSurgery.query().first().organTree

    const traverser = function(nodes, site) {
      for (const node of nodes) {
        if (node.id === site) return node.label
        if (node.children) {
          const childrenResult = traverser(node.children, site)
          if (childrenResult) return childrenResult
        }
      }
      return null
    }
    return traverser(tree, diagnosis) || ''
  }
}

export class Malignancy extends Model {
  static entity = 'malignancies'
  static primaryKey = 'id'

  static fields() {
    return {
      id: this.string(),
      name: this.string()
    }
  }
}

export class DiagnosisQualifiers extends Model {
  static entity = 'qualifiers'
  static primaryKey = 'id'

  static fields() {
    return {
      id: this.string(),
      label: this.string(),
      group: this.string() // maybe make a group model with just id?
    }
  }
}

export class SurgeryDiagnosis extends Model {
  static entity = 'diagnoses'
  static primaryKey = 'id'

  static fields() {
    return {
      id: this.string(),
      label: this.string(),
      icd10: this.string(),
      region: this.string(),
      parent_id: this.attr(null),
      malignancy_ids: this.attr([]),
      complete: this.attr(null),
      malignancy: this.hasManyBy(Malignancy, 'malignancy_ids'),
      qualifier_ids: this.attr([]),
      qualifiers: this.hasManyBy(DiagnosisQualifiers, 'qualifier_ids'),
      children: this.hasMany(SurgeryDiagnosis, 'parent_id')
    }
  }
}

export class SurgeryProcedure extends Model {
  static entity = 'procedures'
  static primaryKey = 'id'

  static fields() {
    return {
      id: this.string(),
      label: this.string(),
      icd10: this.string(),
      region: this.string(),
      parent_id: this.attr(null),
      complete: this.attr(null),
      qualifier_ids: this.attr([]),
      qualifiers: this.hasManyBy(DiagnosisQualifiers, 'qualifier_ids'),
      children: this.hasMany(SurgeryProcedure, 'parent_id')
    }
  }
}
